import { sendEmail } from "@/utils/email/api";

/**
 * Notify the user (browser notification + email) that they've run out of SMS credits.
 * Guarded by localStorage per billing cycle so we send at most one alert per cycle.
 */
export const notifySmsQuotaExceeded = async (params: {
  userId?: string;
  userEmail?: string;
  cycleEnd?: string;
  businessName?: string;
}) => {
  const { userId, userEmail, cycleEnd, businessName } = params;
  const key = `sms_quota_alert_${userId ?? "anon"}_${cycleEnd ?? "current"}`;
  if (typeof window !== "undefined" && window.localStorage.getItem(key)) return;

  // In-app browser notification (best effort)
  try {
    if (typeof window !== "undefined" && "Notification" in window) {
      let perm = Notification.permission;
      if (perm === "default") {
        perm = await Notification.requestPermission();
      }
      if (perm === "granted") {
        new Notification("You're out of SMS credits", {
          body: "Top up to keep texting, or send by email — emails are unlimited and free.",
          icon: "/favicon.ico",
        });
      }
    }
  } catch (err) {
    console.warn("Could not show browser notification", err);
  }

  // Email alert (best effort)
  if (userEmail) {
    try {
      const brand = businessName || "Hound Payments";
      const html = `
        <div style="font-family:Arial,sans-serif;max-width:560px;margin:0 auto;padding:24px;color:#111;">
          <h2 style="margin:0 0 12px;">You're out of SMS credits</h2>
          <p style="margin:0 0 12px;line-height:1.5;">
            Hi${businessName ? ` from ${brand}` : ""}, you've used all your SMS for this billing cycle.
          </p>
          <p style="margin:0 0 12px;line-height:1.5;">
            <strong>Good news:</strong> sending payment requests, receipts and reminders by
            <strong>email is unlimited and free</strong> — you can keep collecting payments without
            paying anything extra.
          </p>
          <p style="margin:0 0 16px;line-height:1.5;">
            If you'd rather keep texting, you can top up another 50 SMS for £9.99 (credits roll over
            and never expire):
          </p>
          <p style="margin:0 0 16px;">
            <a href="https://houndpayments.co.uk/account/subscription#sms-usage"
               style="display:inline-block;background:#16a34a;color:#fff;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600;">
              Top up SMS
            </a>
          </p>
          <p style="margin:0;color:#666;font-size:12px;">
            Your SMS allowance resets at the start of your next billing cycle.
          </p>
        </div>`;
      await sendEmail({
        to: userEmail,
        subject: "You're out of SMS credits — email sending is still free & unlimited",
        html,
        from: "Hound Payments <notifications@houndpayments.co.uk>",
        userId,
      });
    } catch (err) {
      console.warn("Could not send SMS quota alert email", err);
    }
  }

  try {
    window.localStorage.setItem(key, new Date().toISOString());
  } catch {
    /* ignore */
  }
};
